<template>
  <div class="insights-ai-usage-trend-widget insights-widget">
    <div class="widget-wrapper">
      <div class="widget-common widget-large widget-full-height">
        <div class="widget-title d-flex align-items-baseline">
          Estimated cost over time
          <i
            v-tooltip="tooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="infoTooltip"
          />
          <span class="trend-legend ml-auto d-flex align-items-center gap-15">
            <span
              v-for="item in legendItems"
              :key="item.label"
              class="d-flex align-items-center"
            >
              <span
                class="dot mr-5"
                :style="{ backgroundColor: item.color }"
              />
              {{ item.label }}
            </span>
          </span>
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <template v-if="isReady">
            <InsightsNoData
              v-if="isEmptyWidget"
              :type="emptyReason"
            />
            <div
              v-else
              class="chart-container"
            >
              <AppChartStacked
                chart-id="ai-usage-trend"
                class="d-flex align-items-end"
                :sections="chartSections"
                :chart-data="chartData"
                :color-range="colorRange"
                show-popovers
                popover-origin-position="column"
                popover-trigger="mouseover"
                has-y-axis
                :tick-count="4"
                popover-strategy="container"
                :arrow-size="5"
              >
                <template #popover-content="{ columnData }">
                  <div class="p-10 text-left">
                    <div>Est. cost: <b>${{ columnData.cost }}</b></div>
                    <div>Claude ${{ columnData.claude_cost }} · OpenAI ${{ columnData.openai_cost }}</div>
                    <div>{{ columnData.io_tokens }} in+out tokens · {{ columnData.events }} events</div>
                  </div>
                </template>
              </AppChartStacked>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { INSIGHTS } from '~/app_constants';
import { numericValue } from '~/utils/insights_utils';
import { formatDate } from '~/utils/time_utils';
import { Insights } from '~/mixins';
import { mapGetters } from 'vuex';
import AppChartStacked from '~/components/AppChartStacked.vue';

const CLAUDE_COLOR = '#4468d8';
const OPENAI_COLOR = '#17a36d';

export default {
  name: 'InsightsAiUsageTrendWidget',
  components: {
    AppChartStacked,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights],
  data: () => ({
    widgetKey: INSIGHTS.api.aiUsage.trend,
    tooltipOptions: Object.freeze({ html: true }),
    colorRange: [CLAUDE_COLOR, OPENAI_COLOR],
    infoTooltip:
      'Estimated cost per day (per hour for a single-day range) split by provider. ' +
      'API rate-card equivalent — a usage benchmark, not actual subscription spend.',
  }),
  computed: {
    ...mapGetters({ widgetData: 'aiUsageTrend' }),
    chartSections() {
      return ['claude', 'openai'];
    },
    legendItems() {
      return [
        { label: 'Claude', color: CLAUDE_COLOR },
        { label: 'OpenAI', color: OPENAI_COLOR },
      ];
    },
    chartData() {
      return (this.widgetData?.chart || []).map((item) => {
        const cost = +(item.claude_cost + item.openai_cost).toFixed(2);
        const label = this.bucketLabel(item.date);

        // The popover fields live at the top level: the mixin spreads this item into the popover's
        // columnData but overwrites `details` with the section list, and `label` doubles as its title.
        return {
          label,
          value: cost,
          cost,
          claude_cost: item.claude_cost,
          openai_cost: item.openai_cost,
          io_tokens: numericValue(item.io_tokens, { maxNumberBeforeSymbol: 9999, roundDigits: 1 }),
          events: item.events,
          // A zero-cost bucket keeps one full-fraction section: the mixin drops zero-value sections
          // and d3.max of an empty list is undefined, which paints NaN-height popover rects.
          claude: { value: cost ? item.claude_cost / cost : 1, label: '' },
          openai: { value: cost ? item.openai_cost / cost : 0, label: '' },
        };
      });
    },
  },
  methods: {
    bucketLabel(date) {
      // Hourly buckets arrive as 'YYYY-MM-DD HH:00', daily ones as 'YYYY-MM-DD'.
      return date.includes(':') ? date.slice(11) : formatDate(dayjs(date));
    },
  },
};
</script>

<style lang="scss">
.insights-ai-usage-trend-widget {
  .trend-legend {
    font-size: 12px;
    font-weight: normal;
    color: #6b7280;

    .dot {
      display: inline-block;
      width: 9px;
      height: 9px;
      border-radius: 50%;
    }
  }

  .chart-container {
    height: 260px;

    .chart-stacked {
      width: 100%;
      height: 100%;

      .svg-wrapper {
        height: 100%;
      }
    }
  }
}
</style>
